import axios, { AxiosInstance } from "axios";
import { auth } from "../lib/firebase";
import { getConfig } from "../config/environment";
import { logger } from "../utils/logger";

const { cloudFunctions } = getConfig();

/**
 * Attaches the Firebase ID token as a Bearer auth header when a user is
 * signed in. Mirrors speculo-app/src/api/axios.ts so backend handlers can
 * verify the IDX user uniformly.
 */
export const addAuthInterceptor = (instance: AxiosInstance): void => {
  instance.interceptors.request.use(
    async (config) => {
      try {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        logger.error("Error attaching Firebase auth token", { error });
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

/**
 * Builds an axios instance pointing at the given base URL with the Firebase
 * auth interceptor wired up. Use for any URL that isn't a named Cloud Run
 * service (e.g. a Cloud Function path).
 */
export const createAuthenticatedAxios = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    headers: { "Content-Type": "application/json" },
  });
  addAuthInterceptor(instance);
  return instance;
};

/**
 * Builds an axios instance for a named Cloud Run service, resolving its base
 * URL from env config and wiring the Firebase auth interceptor. Mirrors
 * speculo-app's `createCloudRunAxios(serviceName)` so every service client is
 * created the same way — call `createCloudRunAxios('aiSearch')` etc.
 *
 * Note: unlike speculo-app (which derives URLs from a single `projectNumber`),
 * search-experience keeps an explicit per-service URL map in
 * `config/environment.ts` because its deployed Cloud Run URLs aren't uniform
 * (some use hash-form `*.a.run.app` hosts). This factory reuses that map.
 */
export const createCloudRunAxios = (
  service: keyof ReturnType<typeof getConfig>["cloudRunServices"]
): AxiosInstance => {
  return createAuthenticatedAxios(getConfig().cloudRunServices[service]);
};

/**
 * Pre-built axios instance for the current env's Cloud Functions base URL.
 * Use for searchApi (valuation) and mls-logo-compliance.
 */
export const cloudFunctionsAxios = axios.create({
  baseURL: cloudFunctions.baseURL,
  headers: { "Content-Type": "application/json" },
});

addAuthInterceptor(cloudFunctionsAxios);
